import React, { useRef, useState } from 'react';
import hooks from 'shared/hooks';
import styled from 'styled-components';

import { theme } from '../../../../theme';
import { useMediaSize } from '../../hooks';
import { TooltipDirection } from '../tooltip/BaseTooltip';
import Tooltip  from '../tooltip/Tooltip';

export interface IconProps {
    className?: string;
    disabled?: boolean;
    color?: string;
    icon: React.ReactNode;
    onClick?: (event: React.MouseEvent) => void;
    size?: number;
    tooltipText?: string;
    tooltipDirection?: TooltipDirection;
}

const Icon: React.FC<IconProps> = (props): JSX.Element => {
    const {
        className = '',
        disabled,
        color = theme.color.grey.grey_50,
        icon,
        onClick,
        size = 16,
        tooltipText,
        tooltipDirection = TooltipDirection.top
    } = props;

    const ref = useRef<HTMLButtonElement>(null);
    const [ visible, setVisible ] = useState<boolean>(false);
    const [ mobileClickHandlingAvailable, setMobileClickHandlingAvailable ] = useState(false);

    const { isDesktop } = useMediaSize();

    const processIntersection = (intersecting: boolean) => {
        if ( !intersecting ) {
            setVisible(false);
        }
    };

    hooks.interactions.useScrollAreaIntersection(ref.current || null, processIntersection, visible);

    const handleMouseEnter = () => setVisible(true);

    const handleMouseLeave = () => {
        setMobileClickHandlingAvailable(false);
        setVisible(false);
    };

    const tooltipPresent = Boolean((tooltipText??'').trim());

    const handleMobileClick = (event: React.MouseEvent) => {
        if (!mobileClickHandlingAvailable) {
            setVisible(true);
            setMobileClickHandlingAvailable(true);
        }

        if (mobileClickHandlingAvailable && onClick) {
            onClick(event);
        }
    };

    const handleClick = (event: React.MouseEvent) => {
        if (!isDesktop && tooltipPresent) {
            handleMobileClick(event);

            return;
        }

        if (!onClick) {
            return;
        }

        onClick(event);
    };

    return (<Root className={ className }
        onMouseEnter={ handleMouseEnter }
        onMouseLeave={ handleMouseLeave }
        onTouchStart={ handleMouseEnter }
        interactive={ Boolean(onClick) }
        ref={ ref }
        size={ size }
        color = { color }
        onClick={ handleClick }
        as={ onClick ? 'button' : undefined }
        disabled={ disabled }
    >
        <HoverBkg />

        { icon }

        {  ref.current && tooltipPresent &&
                <Tooltip parent={ ref.current }
                    direction={ tooltipDirection }
                    offset={ 12 }
                    visible={ visible }
                >
                    { tooltipText }
                </Tooltip>
        }
    </Root>);
};

export default styled(Icon)``;

interface RootProps {
    color: string;
    interactive: boolean;
    size: number;
    disabled?: boolean;
}

const HoverBkg = styled.div`
    border-radius: 100%;
    display: none;
    left: -${ ({ theme }) => theme.size.size_5 };
    opacity: 0.1;
    position: absolute;
    top: -${ ({ theme }) => theme.size.size_5 };
`;

const Root = styled.div<RootProps>`
    display: flex;
    flex-shrink: 0;
    position: relative;
    border: none;
    background: none;
    padding: 0;

    ${ ({ size }) => `
        height: ${ size }px;
        width: ${ size }px;
    ` }

    ${ ({ interactive, disabled }) => (interactive && !disabled && 'cursor: pointer;') }

    &:hover {
        ${ HoverBkg } {
            ${ ({ size, color, theme, disabled }) => !disabled && `
                background-color: ${ color };
                display: inline-block;
                height: calc(${ size }px + ${ theme.size.size_10 });
                width: calc(${ size }px + ${ theme.size.size_10 });
            ` }
        }
    }

    svg {
        fill: ${ ({ color }) => color };
        height: 100%;
        path { fill: ${ ({ color }) => color } };
        width: 100%;
    }
`;
